import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, Send, Quote, User, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 25 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

const StarRating = ({ rating, interactive = false, onChange }) => (
  <div className="flex gap-0.5" data-testid="star-rating">
    {[1, 2, 3, 4, 5].map(s => (
      <button key={s} type="button" disabled={!interactive}
        onClick={() => interactive && onChange?.(s)}
        className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}>
        <Star size={interactive ? 22 : 16}
          className={s <= rating ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'} />
      </button>
    ))}
  </div>
);

const TestimonialCard = ({ t, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-30px" }}
    transition={{ delay: index * 0.08, duration: 0.5 }}
    className="bg-card border border-border rounded-lg p-6 sm:p-8 relative group hover:border-accent/30 transition-colors"
    data-testid={`testimonial-card-${index}`}
  >
    <Quote size={28} className="text-accent/15 absolute top-4 right-4" />
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
        <User size={18} className="text-accent" />
      </div>
      <div>
        <h3 className="font-sans text-sm font-semibold">{t.client_name}</h3>
        {t.designation && <p className="font-sans text-xs text-muted-foreground">{t.designation}</p>}
      </div>
    </div>
    <StarRating rating={t.rating || 5} />
    <p className="font-sans text-sm sm:text-base leading-relaxed text-foreground/80 mt-3">
      "{t.testimonial_text}"
    </p>
    {t.project_type && (
      <span className="inline-block mt-3 px-2 py-0.5 bg-accent/10 text-accent text-[10px] uppercase tracking-wider rounded-full font-medium">
        {t.project_type}
      </span>
    )}
  </motion.div>
);

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ client_name: '', testimonial_text: '', designation: '', email: '', rating: 5 });

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(r => setTestimonials(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim() || !form.testimonial_text.trim()) {
      toast.error('Please fill in your name and review');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials/submit`, form);
      setSubmitted(true);
      toast.success('Thank you! Your review will appear after approval.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 sm:pt-32 pb-16 sm:pb-24" data-testid="testimonials-page">
      <SEO
        title="Testimonials - Gangadhar Som"
        description="Read what collectors and admirers say about the artwork of Gangadhar Som."
        url={`${window.location.origin}/testimonials`}
      />
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }} className="mb-12 sm:mb-16">
          <motion.span variants={fadeUp} className="font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground">
            What People Say
          </motion.span>
          <motion.h1 variants={fadeUp} className="font-serif text-3xl sm:text-4xl md:text-6xl font-light tracking-tight mt-3 mb-5"
            data-testid="testimonials-title">
            Testimonials
          </motion.h1>
          <motion.p variants={fadeUp} className="font-sans text-sm sm:text-base md:text-lg text-foreground/80 max-w-2xl">
            Hear from collectors, art enthusiasts, and patrons who have experienced the artwork of Gangadhar Som.
          </motion.p>
        </motion.div>

        {/* Testimonials Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-card border border-border rounded-lg p-8 animate-pulse">
                <div className="h-4 bg-muted rounded w-1/3 mb-4" />
                <div className="h-3 bg-muted rounded w-full mb-2" />
                <div className="h-3 bg-muted rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : testimonials.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-6 mb-16">
            {testimonials.map((t, i) => <TestimonialCard key={t.id} t={t} index={i} />)}
          </div>
        ) : (
          <div className="text-center py-16 mb-16">
            <Quote size={40} className="mx-auto text-muted-foreground/20 mb-4" />
            <p className="font-sans text-muted-foreground">No testimonials yet. Be the first to share your experience!</p>
          </div>
        )}

        {/* Submit Review Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-card border border-border rounded-lg p-6 sm:p-8 md:p-10 max-w-2xl mx-auto"
          data-testid="submit-review-section"
        >
          {submitted ? (
            <div className="text-center py-6">
              <CheckCircle size={40} className="mx-auto text-green-500 mb-4" />
              <h3 className="font-serif text-xl sm:text-2xl font-light mb-2">Thank You!</h3>
              <p className="font-sans text-sm text-muted-foreground">
                Your review has been submitted and will appear after approval by the site owner.
              </p>
            </div>
          ) : !showForm ? (
            <div className="text-center">
              <h3 className="font-serif text-xl sm:text-2xl font-light mb-3">Share Your Experience</h3>
              <p className="font-sans text-sm text-muted-foreground mb-5">
                Have you collected or experienced the artwork of Gangadhar Som? We'd love to hear from you.
              </p>
              <Button onClick={() => setShowForm(true)} className="bg-accent hover:bg-accent/90"
                data-testid="write-review-btn">
                <Star size={14} className="mr-2" /> Write a Review
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <h3 className="font-serif text-xl font-light mb-1">Write a Review</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Your Name *</Label>
                  <Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })}
                    placeholder="John Doe" required data-testid="review-name" />
                </div>
                <div>
                  <Label className="text-xs">Designation / Location</Label>
                  <Input value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })}
                    placeholder="Art Collector, Mumbai" data-testid="review-designation" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Email (optional, won't be displayed)</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder="your@email.com" data-testid="review-email" />
              </div>
              <div>
                <Label className="text-xs">Rating</Label>
                <StarRating rating={form.rating} interactive onChange={r => setForm({ ...form, rating: r })} />
              </div>
              <div>
                <Label className="text-xs">Your Review *</Label>
                <Textarea value={form.testimonial_text} onChange={e => setForm({ ...form, testimonial_text: e.target.value })}
                  placeholder="Share your experience with the artwork..." rows={4} required
                  data-testid="review-text" />
              </div>
              <div className="flex gap-3">
                <Button type="submit" disabled={submitting} className="bg-accent hover:bg-accent/90"
                  data-testid="submit-review-btn">
                  <Send size={14} className="mr-2" /> {submitting ? 'Submitting...' : 'Submit Review'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Testimonials;
